import React from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CircularProgress, Box } from "@mui/material";
import { ClerkProvider, SignedIn, SignedOut } from "@clerk/clerk-react";
import theme from "./theme";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import PipelineSearch from "./pages/PipelineSearch";
import CandidateDetail from "./pages/CandidateDetail";
import Companies from "./pages/Companies";
import Alerts from "./pages/Alerts";
import DataSync from "./pages/DataSync";
import ContactSearch from "./pages/ContactSearch";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { initAnalytics } from "./services/analytics";

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || "";

// Initialize PostHog analytics
initAnalytics();

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!isAdmin) navigate("/", { replace: true });
  }, [isAdmin, navigate]);
  return isAdmin ? <>{children}</> : null;
}

function AppRoutes() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/pipeline" element={<PipelineSearch />} />
        <Route path="/pipeline/:id" element={<CandidateDetail />} />
        <Route path="/contacts" element={<ContactSearch />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/alerts" element={<Alerts />} />
        <Route path="/sync" element={<AdminRoute><DataSync /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <SignedOut>
            <Login />
          </SignedOut>
          <SignedIn>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </SignedIn>
        </BrowserRouter>
      </ThemeProvider>
    </ClerkProvider>
  );
}

export default App;
